import React, {useEffect, useState} from "react";
import "./login.css";
import { Button, Input } from "antd";
import LogoDark from "../../assets/images/logo-dark.png";
import {ArrowRightOutlined, LoginOutlined, RightOutlined} from "@ant-design/icons";
import {login} from "../../api/login";
import useUserContext from "../../hooks/useUserContext";
import {Navigate} from "react-router-dom";
import Loader from "../../components/loader/Loader";
import useAppContext from "../../hooks/useAppContext";
import LoginIcon from "../../assets/icons/login-icon.png";
import {verifyLoginCode} from "../../api/verifyLoginCode";
import background from "../../assets/images/balloons-background-2.jpg";
import {LoginIllustration1} from "../../assets/illustrations/LoginIllustration1";
import {IdeaIcon} from "../../assets/icons/IdeaIcon";
import VuelveLogo from "../../assets/images/logo-bright.png";
import OtpCode from "../../components/forms/otpCode/OtpCode";

export const QUOTES = [
    { text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
    { text: "Your most unhappy customers are your greatest source of learning.", author: "Bill Gates" },
    { text: "Opportunities don’t happen. You create them.", author: "Chris Grosser" },
    { text: "The best marketing doesn’t feel like marketing.", author: "Tom Fishburne" },
    { text: "Success usually comes to those who are too busy to be looking for it.", author: "Henry David Thoreau" },
    { text: "Customer service shouldn’t just be a department, it should be the entire company.", author: "Tony Hsieh" },
    { text: "It’s not what you say, but how you make people feel, that matters.", author: "Maya Angelou" },
    { text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
    { text: "Communication leads to community, that is, to understanding, intimacy and mutual valuing.", author: "Rollo May" },
    { text: "In the middle of every difficulty lies opportunity.", author: "Albert Einstein" },
];

export const STATS = [
    "98% of WhatsApp messages are read within 3 minutes.",
    "SMS has a 98% open rate, compared to ~20% for email.",
    "The average WhatsApp response time is less than 5 minutes.",
    "Businesses using WhatsApp see up to 27% higher conversion rates on follow-ups.",
    "SMS click-through rates can reach 19%, compared to 2–3% for email.",
    "Over 175 million people message a WhatsApp Business account every day.",
    "70% of customers feel more connected to a business when they can message it directly.",
    "More than 65% of WhatsApp users check the app multiple times per day.",
    "Automated WhatsApp reminders reduce missed appointments by up to 30%.",
    "Customers are 53% more likely to purchase from businesses they can message directly.",
];



const Login = () => {
    const { user, isInitialLoadDone, actions: { getData } } = useUserContext();
    const appContext = useAppContext();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [step, setStep] = useState(1);
    const [error, setError] = useState('');
    const [quote, setQuote] = useState('');
    const [speaker, setSpeaker] = useState('');
    const [stats, setStats] = useState('');
    const [textToShow, setTextToShow] = useState(0);
    const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    useEffect(() => {
        const quoteIndex = Math.floor(Math.random()*10);
        const statsIndex = Math.floor(Math.random()*10);
        const statOrQuote = Math.floor(Math.random()*2);
        const quote = QUOTES[quoteIndex];
        setQuote(quote.text);
        setSpeaker(quote.author);
        setStats(STATS[statsIndex]);
        setTextToShow(statOrQuote);
    }, []);

    const onSendMail = async () => {
        if(!isValidEmail(email)) {
            setError('The email address you entered is not valid.');
            return;
        }
        const response = await login(email);
        if(response.data === 'ok') {
            setStep(2);
        } else if(response.data === 'no mail') {
            setError('The email address you entered does not exist in our records. Please verify and try again.');
        }
    }

    const onSendCode = async () => {
        if(code.length < 6) {
            setError('This code is wrong');
            return;
        }
        const response = await verifyLoginCode(email, code);
        if(response.data.isCorrect) {
            setUsername(response.data.user.name);
            localStorage.setItem('token', response.data.user.token);
            getData();
        } else {
            if(response.data.attemptsLeft === 0) {
                setCode('');
                setEmail('');
                setStep(1);
                setError('');
            } else {
                setError(`This code is wrong (${response.data.attemptsLeft} tries left)`);
            }
        }
    }

    if(user) {
        return <Navigate to="/" replace />
    }

    if(username) {
        return <div className="welcome-message">
            <div className="overlay" />
            <div className="welcome-message-content">
                <div className="username">Hey {username},</div>
                <div>In just a few seconds you'll be redirected to your dashboard</div>
                <Loader size={100} isFullPage={false} />
            </div>
        </div>
    }

    return <div className="login-next-container">
        <div className="bg" />
        <div className="login-next-inner">
            <div className="form-container">
                <div className="form-inner">
                    <div className="title">
                        <div>Welcome to</div>
                        <img src={VuelveLogo} width={240} />
                        <div className="sub-title">Stay connected with what matters most.</div>
                    </div>
                    {step === 1 ? <>
                        <div className="field-title">Enter your email to get started:</div>
                        <div className="field">
                            <Input placeholder="Email" className="email-field" value={email} onChange={(event) => {
                                setError('');
                                setEmail(event.target.value);
                            }} />
                            <Button icon={<RightOutlined />} type="primary" className="step1-button" onClick={onSendMail} disabled={!isValidEmail(email)} />
                        </div>
                        {error ? <div className="error">{error}</div> : null}
                    </> : step === 2 ? <>
                        <div className="field-title">Enter the verification code we sent to your email:</div>
                        <div className="field">
                            <OtpCode onChange={(newVal: string) => {
                                setCode(newVal);
                            }} />
                        </div>
                        <Button icon={<RightOutlined />} type="primary" className="step2-button" onClick={onSendCode} iconPosition="end" size="large" disabled={code.length !== 6}>Submit</Button>
                        <Button className="step2-link" type="link" onClick={() => {
                            setCode('');
                            setEmail('');
                            setStep(1);
                        }}>Re-write your email address</Button>
                    </> : null}
                </div>
                <div className="login-intro"><div className="login-intro-inner" /></div>
            </div>
            <div className="login-intro">
                <div className="login-intro-inner">
                    {textToShow === 1 ?
                        <div className="quote-container">
                            <div className="quote">"{quote}"</div>
                            <div className="speaker">{speaker}</div>
                        </div>
                    : <div className="stats-container">
                            Did you know that <div className="stats">{stats}</div>
                    </div>}
                    {/*<LoginIllustration1 width={160} color='#fff' />*/}
                </div>
            </div>
        </div>
    </div>
}

export default Login;